/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * CÂY TÀI KHOẢN — luật THUẦN, không React, không mạng. (lee đặt hàng lượt 58, việc 2.)
 *
 * 🔴 VÌ SAO TÁCH RA MỘT TỆP RIÊNG, không viết thẳng trong `QuanTri.tsx`: cái khó của việc này
 *   KHÔNG nằm ở chỗ vẽ — nó nằm ở chỗ **xếp**. Bốn ca dễ đọc nhầm nhau:
 *
 *     `createdById === null` + `SUPER_ADMIN`   → GỐC THẬT (mức 0, không huy hiệu)
 *     `createdById === null` + hạng khác       → MỒ CÔI, ca `@jlee` (mức 0, huy hiệu hổ phách)
 *     `createdById` trỏ ra NGOÀI danh sách     → gốc CỦA KHUNG NHÌN NÀY (khách đăng nhập:
 *                                                 người tạo họ không nằm trong cây con của họ)
 *     `createdById` trỏ vào một hàng có mặt    → con, thụt vào
 *
 *   Ba ca đầu đều "hiện ở mức 0" — và đó đúng là chỗ dễ gộp bậy. Gộp ca 2 vào ca 1 là bày một
 *   tài khoản mồ côi ra như một gốc thật, tức là **giấu mất đúng cái lee muốn nhìn thấy**; gộp
 *   ca 3 vào ca 2 là dán chữ *"không có người tạo"* lên một tài khoản có người tạo hẳn hoi, chỉ
 *   là người ấy ở trên tầm mắt của người đang xem. Luật số 0 lần nữa: **thiếu ≠ rỗng ≠ không**.
 *
 * ⛔ VÀ VÌ TÁCH RA, MỤC `[96]` CỦA `verify-protocol.ts` **GỌI ĐƯỢC** hàm này với dữ liệu dựng
 *   sẵn — đo cái CỔNG chứ không quét mặt chữ. Một phép đếm `'không có người tạo'` có xuất hiện
 *   trong `QuanTri.tsx` hay không thì không biết gì về việc nó dán đúng hàng nào.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */

/** Đủ để xếp cây. Cố ý hẹp: mọi thứ khác là việc của chỗ VẼ, không phải của luật xếp. */
export interface HangCoNguoiTao {
  id: string;
  username: string;
  role: string;
  createdById: string | null;
}

export interface NutCay<T extends HangCoNguoiTao> {
  hang: T;
  /** 0 = hiện ở lề trái. Con của mức `n` là mức `n+1`. */
  mucSau: number;
  /** Số tài khoản do hàng này trực tiếp tạo **và có mặt trong danh sách**. */
  soConTrucTiep: number;
  /** Cả nhánh dưới (cháu chắt), để nói được "đang ẩn N tài khoản" khi thu. */
  soHauDue: number;
  /**
   * ĐÚNG ca `@jlee`: không có người tạo mà **không phải** `SUPER_ADMIN`.
   * ⚠ KHÔNG bật cho ca "người tạo ở ngoài danh sách" — xem chú thích đầu tệp.
   */
  moCoi: boolean;
  /**
   * Với TỪNG mức tổ tiên (kể cả chính nút, ở ô cuối): tổ tiên ấy có phải con ÚT của cha nó
   * không. Chỗ vẽ đọc mảng này ra `│  ` / `   ` / `├─` / `└─` — nhánh không đứt giữa chừng.
   */
  oCuoi: boolean[];
}

/**
 * Xếp danh sách phẳng thành cây, trả về **thứ tự đã duyệt sâu** (cha rồi tới nhánh con).
 *
 * @param ds    danh sách máy chủ trả (đã là cây con của người gọi + chính họ).
 * @param idToi id của người đang đăng nhập — chỉ dùng để **đưa họ lên đầu** trong đám gốc.
 *
 * ⚠ CHỐNG VÒNG. Dữ liệu thật không nên có vòng (`a` tạo `b`, `b` tạo `a`), nhưng một vòng ở đây
 *   không phải một hàng vẽ sai — nó là một vòng lặp vô hạn treo cả trang. Tôi giữ `daXep` và
 *   những hàng không tới được thì **thả xuống cuối ở mức 0**: thà bày ra một hàng khó hiểu còn
 *   hơn làm mất nó, vì hàng bị mất là hàng không ai đi tìm.
 */
export function xepCay<T extends HangCoNguoiTao>(ds: readonly T[], idToi?: string | null): NutCay<T>[] {
  const coMat = new Set(ds.map((u) => u.id));
  const conTheoCha = new Map<string, T[]>();
  const goc: T[] = [];

  for (const u of ds) {
    /* Cha KHÔNG có mặt trong khung nhìn ⇒ hàng này là gốc CỦA KHUNG NHÌN (ca 3). */
    if (u.createdById && coMat.has(u.createdById) && u.createdById !== u.id) {
      const m = conTheoCha.get(u.createdById);
      if (m) m.push(u); else conTheoCha.set(u.createdById, [u]);
    } else {
      goc.push(u);
    }
  }

  const theoTen = (a: T, b: T) => a.username.localeCompare(b.username);
  for (const m of conTheoCha.values()) m.sort(theoTen);
  /* Tôi đứng đầu đám gốc — khách mở màn hình phải thấy mình ngay dòng một. */
  goc.sort((a, b) => (a.id === idToi ? -1 : b.id === idToi ? 1 : theoTen(a, b)));

  const ra: NutCay<T>[] = [];
  const daXep = new Set<string>();

  /** Đếm cả nhánh dưới. Tách khỏi vòng duyệt vì `soHauDue` phải biết TRƯỚC khi vẽ dòng cha. */
  const demHauDue = (id: string, tham = new Set<string>()): number => {
    if (tham.has(id)) return 0;
    tham.add(id);
    let n = 0;
    for (const c of conTheoCha.get(id) ?? []) n += 1 + demHauDue(c.id, tham);
    return n;
  };

  const duyet = (u: T, oCuoi: boolean[]) => {
    if (daXep.has(u.id)) return;
    daXep.add(u.id);
    const con = conTheoCha.get(u.id) ?? [];
    ra.push({
      hang: u,
      mucSau: oCuoi.length,
      soConTrucTiep: con.length,
      soHauDue: demHauDue(u.id),
      moCoi: u.createdById === null && u.role !== 'SUPER_ADMIN',
      oCuoi,
    });
    con.forEach((c, i) => duyet(c, [...oCuoi, i === con.length - 1]));
  };

  /* Gốc: `oCuoi` RỖNG — mức 0 không có gạch nhánh nào để vẽ, kể cả gốc cuối cùng. */
  for (const u of goc) duyet(u, []);

  /* Hàng không tới được (vòng) — thả xuống cuối, mức 0, chứ không bỏ. */
  for (const u of ds) {
    if (daXep.has(u.id)) continue;
    daXep.add(u.id);
    ra.push({ hang: u, mucSau: 0, soConTrucTiep: 0, soHauDue: 0, moCoi: false, oCuoi: [] });
  }
  return ra;
}

/**
 * Bỏ đi những nút nằm DƯỚI một nút đang thu. Nút đang thu **vẫn hiện** — nó là cái để bấm mở.
 *
 * ⚠ Không hỏi "cha tôi có bị thu không" (phải tra ngược), mà đi theo THỨ TỰ ĐÃ DUYỆT SÂU: đã
 *   vào một nhánh thu thì mọi dòng sâu hơn đều thuộc nhánh ấy, tới khi gặp một dòng NÔNG BẰNG
 *   hoặc nông hơn. Một vòng `for`, không đệ quy, và không cần bản đồ cha-con thứ hai.
 */
export function locThu<T extends HangCoNguoiTao>(
  ds: readonly NutCay<T>[], dangThu: ReadonlySet<string>,
): NutCay<T>[] {
  const ra: NutCay<T>[] = [];
  /** Mức của nút thu NÔNG NHẤT đang bao ta; `-1` = không ở trong nhánh thu nào. */
  let mucThu = -1;
  for (const n of ds) {
    if (mucThu >= 0 && n.mucSau > mucThu) continue;   // còn nằm dưới nút đang thu
    mucThu = -1;                                      // đã ra khỏi nhánh ấy
    ra.push(n);
    if (dangThu.has(n.hang.id) && n.soConTrucTiep > 0) mucThu = n.mucSau;
  }
  return ra;
}

/**
 * Vẽ phần gạch nhánh của một dòng, bằng ký tự — không phải bằng `padding`.
 *
 * ⚠ Vì sao ký tự: `padding-left` cho THỤT VÀO nhưng không cho biết **nhánh nào còn chạy tiếp
 *   xuống dưới**. Với cây ba tầng, hai hàng thụt bằng nhau có thể thuộc hai cha khác nhau, và
 *   không có `│` thì không cách nào nhìn ra.
 */
export function gachNhanh(oCuoi: readonly boolean[]): string {
  if (oCuoi.length === 0) return '';
  return oCuoi
    .map((cuoi, i) => (i === oCuoi.length - 1 ? (cuoi ? '└─ ' : '├─ ') : (cuoi ? '   ' : '│  ')))
    .join('');
}
